import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FiEye, FiDownload } from "react-icons/fi";
import { LuFileText } from "react-icons/lu";
import {
  getPolicyCategories,
  getPoliciesList,
} from "../../ApiHelper/url_Helpers";
import Loader from "../../Components/Loader";

export const CompanyPolicies = () => {
  const [tabs, setTabs] = useState([]);
  const [activeTab, setActiveTab] = useState(null);
  const [policies, setPolicies] = useState([]);
  const [admin, setAdmin] = useState({});
  const [loading, setLoading] = useState(false);
  const [recentUpdates, setRecentUpdates] = useState([]);

  const navigate = useNavigate();
  const { categoryName } = useParams();

  // convert category name to URL-route
  const toSlug = (name) => name.toLowerCase().replace(/\s+/g, "-");
  const hrEmail = admin?.email || " ";

  // 1. Fetch Categories
  useEffect(() => {
    const fetchTabs = async () => {
      try {
        const res = await getPolicyCategories();
        if (res && Array.isArray(res) && res.length > 0) {
          setTabs(res);
          if (!categoryName) {
            navigate(`/company-policies/${toSlug(res[0].name)}`, { replace: true });
          } else {
            const matchedTab = res.find(t => toSlug(t.name) === categoryName);
            if (matchedTab) {
              setActiveTab(matchedTab);
            } else {
              navigate(`/company-policies/${toSlug(res[0].name)}`, { replace: true });
            }
          }
        }
      } catch (err) {
        console.error("Tabs error:", err);
      }
    };
    fetchTabs();
  }, [categoryName, navigate]);

  useEffect(() => {
    if (tabs.length > 0 && categoryName) {
      const matchedTab = tabs.find(t => toSlug(t.name) === categoryName);
      if (matchedTab) {
        setActiveTab(matchedTab);
      }
    }
  }, [categoryName, tabs]);

  const fetchPolicies = useCallback(async () => {
    if (!activeTab?.id) return;
    try {
      setLoading(true);
      const res = await getPoliciesList(activeTab.id);
      const data = res || [];
      setAdmin(res?.hr_contact)
      setPolicies(data?.policies);
      if (data?.policies.length > 0) {
        const sorted = [...data?.policies]
          .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
          .slice(0, 3);
        setRecentUpdates(sorted);
      }
    } catch (err) {
      console.error("Policies error:", err);
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    fetchPolicies();
  }, [fetchPolicies]);

  // Helper function to format date
  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const options = { year: "numeric", month: "short", day: "numeric" };
    return new Date(dateStr).toLocaleDateString(undefined, options);
  };

  return (
    <div className="w-full mt-5 px-2 sm:px-5 flex flex-col lg:flex-row gap-5">
      <div className="flex-1   px-4">
        <span className="text-xl font-semibold text-gray-800">
          Company Policies
        </span>
        <p className="text-sm mb-5 text-gray-500">
          Access and review all company policies and procedures
        </p>

        {/* DYNAMIC TABS */}
        <div className="flex gap-5 pb-1 font-semibold text-black overflow-x-auto whitespace-nowrap border-b border-gray-100">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => navigate(`/company-policies/${toSlug(tab.name)}`)}
              className={`text-md border-b-[3px] cursor-pointer pb-2 transition-all ${activeTab?.id === tab.id
                ? "border-b-sky-500 font-semibold text-black"
                : "border-transparent text-[#999999]"
                }`}
            >
              {tab.name}
            </button>
          ))}
        </div>

        <div className="flex flex-col lg:flex-row gap-6 mt-2">
          {/* DYNAMIC POLICY LIST */}
          <div className="flex-1 bg-white border border-gray-200 rounded-xl p-4 min-h-[400px]">
            {loading ? (
              <Loader />
            ) : policies.length > 0 ? (
              policies.map((item) => (
                <div
                  key={item.id}
                  className="flex justify-between items-center py-4 border-b-2 border-gray-100 hover:bg-gray-50 px-2 rounded-lg"
                >
                  <div className="flex items-center gap-2">
                    <LuFileText className="text-[#7a8699] text-3xl sm:text-4xl" />
                    <div>
                      <h3 className="font-semibold text-[#4a4a4a] mb-1">
                        {item.title}
                      </h3>
                      <p className="text-gray-500 text-xs font-medium">
                        Effective: {formatDate(item.effective_date)}
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4 text-gray-600">
                    {/* VIEW FILE */}
                    <a href={item.content_file} target="_blank">
                      <FiEye className="cursor-pointer text-lg hover:text-sky-500" />
                    </a>
                  </div>
                </div>
              ))
            ) : (
              <div className="p-10 text-center text-gray-400">
                No policies found for this category.
              </div>
            )}
          </div>

          <div className="lg:w-1/4 w-full flex flex-col gap-6">
            <div className="bg-white rounded-xl border border-gray-300 p-4">
              <h2 className="mb-3 text-base font-medium">Recent Updates</h2>
              {recentUpdates.length > 0 ? (
                recentUpdates.map((item, index) => {
                  const bgColors = ["bg-blue-50", "bg-pink-50", "bg-green-50"];
                  const currentBg = bgColors[index % bgColors.length];

                  return (
                    <div
                      key={item.id}
                      className={`p-3 rounded-lg mb-3 ${currentBg}`}
                    >
                      <p className="text-sm font-medium text-[#1d293d] mb-1">
                        {item.title}
                      </p>
                      <p className="text-[#62748e] text-xs">
                        Updated: {formatDate(item.updated_at)}
                      </p>
                    </div>
                  );
                })
              ) : (
                <p className="text-gray-400 text-sm">No recent updates</p>
              )}
            </div>

            <div className="bg-[#495162] text-white p-6 rounded-xl shadow">
              <h3 className="text-lg font-light">Need Help?</h3>
              <p className="text-sm mt-1 font-light text-[#e0e7ff]">
                Contact HR for clarifications
              </p>
              <div className="flex flex-col gap-2 mt-4">
                <a href={`https://mail.google.com/mail/?view=cm&fs=1&to=${hrEmail}`} target="_blank" rel="noreferrer"
                  className="w-full block text-center font-medium bg-white text-[#ea4335] border border-gray-200 rounded-md py-2 hover:bg-gray-50 transition-colors">
                  Open in Gmail
                </a>
                <a href={`https://outlook.office.com/mail/deeplink/compose?to=${hrEmail}`} target="_blank" rel="noreferrer"
                  className="w-full block text-center font-medium bg-white text-[#0078d4] border border-gray-200 rounded-md py-2 hover:bg-gray-50 transition-colors">
                  Open in Outlook
                </a>
              </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  );
};
